import { useRef, useEffect, useMemo, useCallback, useState, MutableRefObject } from 'react'
import { Camera } from '@/types/colmap'
import { useAppStore } from '@/store/appStore'
import { useNavigationSettings } from '@hooks/useNavigationSettings'
import { CanvasPanControls } from './CanvasPanControls'
import './CandidateFrameList.css'

interface CandidateFrame {
  camera: Camera
  frameIndex: number
  imagePath: string
  isContextFrame?: boolean
  isWinner: boolean
  passedFilters: boolean
}

interface CandidateFrameListProps {
  candidates: CandidateFrame[]
  currentWinnerId: number | undefined
  onBalloonSelect?: (frameIndex: number | null) => void
  onBalloonHover?: (frameIndex: number) => void
  selectedBalloonFrame?: number | null
  onViewportHover?: (frameIndex: number | null) => void
  onBalloonClose?: () => void
  onShowNavigationBalloon?: () => void
  isPanningRef?: MutableRefObject<boolean>
  contextFrameCount?: number
  viewportFrameCount?: number
}

type SortOption = 'connected' | '3d-distance' | 'delta-angle' | null

export function CandidateFrameList({ 
  candidates, 
  currentWinnerId,
  onBalloonSelect,
  onBalloonHover,
  selectedBalloonFrame,
  onViewportHover,
  onShowNavigationBalloon,
  isPanningRef,
  contextFrameCount = 0,
  viewportFrameCount = 0
}: CandidateFrameListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastTapRef = useRef<{ time: number; target: EventTarget | null }>({ time: 0, target: null })
  const balloonPinnedRef = useRef<boolean>(false) // Track if balloon is pinned by click
  const offsetMap = useAppStore((state) => state.offsetMap)
  const connectivityGraph = useAppStore((state) => state.connectivityGraph)
  const [activeSortOption, setActiveSortOption] = useState<SortOption>(null)
  
  // 🆕 Subscribe to navigation settings for filter display
  const { settings: navigationSettings } = useNavigationSettings()
  
  // Memoized callback for canvas panning
  const handleCanvasPan = useCallback((deltaX: number, deltaY: number) => {
    useAppStore.getState().panViewport(deltaX, deltaY)
  }, [])
  
  // Memoized callback for canvas zooming
  const handleCanvasZoom = useCallback((scaleFactor: number) => {
    const state = useAppStore.getState()
    const newScale = state.zoomScale * scaleFactor
    state.setZoomScale(newScale)
  }, [])
  
  // Use shallow equality for memoizing store selectors
  const offsetMapStable = useMemo(() => offsetMap, [offsetMap?.all_pairs]) 
  const connectivityGraphStable = useMemo(() => connectivityGraph, [connectivityGraph?.size])
  
  // Reset pinned flag when balloon is closed from parent
  useEffect(() => {
    if (selectedBalloonFrame === null) {
      balloonPinnedRef.current = false
    }
  }, [selectedBalloonFrame])
  
  // 🎯 ✅ FIXED: Use currentWinnerId directly (fresh data, not frozen)
  // This ensures connectivity recalculates IMMEDIATELY when frame selection changes
  const winnerIdForSorting = currentWinnerId
  
  // Debug log to understand the data flow
  useEffect(() => {
    // Removed debug logging
  }, [currentWinnerId, winnerIdForSorting, activeSortOption, candidates.length])
  
  // 🔧 FIXED: Get RAW connected frame indices - NO distance filtering here
  // Distance/angle filtering belongs in the NAVIGATION FILTERS ONLY (navigationSettings)
  // Not in AppSettings distance/dZ filtering!
  // ✅ CRITICAL: Include winnerIdForSorting in dependencies, NOT frozenWinnerIdRef.current
  // (frozenWinnerIdRef.current is an object, changes don't trigger re-computation)
  const connectedFrameIndices = useMemo(() => {
    const connected = new Set<number>()
    if (winnerIdForSorting === undefined || !connectivityGraphStable) {
      return connected
    }

    const connectedSet = connectivityGraphStable.get(winnerIdForSorting)
    if (!connectedSet) {
      return connected
    }

    // Add all connected frames WITHOUT pre-filtering by distance or dZ
    // Navigation filters (3D Distance, Delta Angle) are applied separately via navigationSettings
    connectedSet.forEach(idx => {
      connected.add(idx)
    })

    return connected
  }, [connectivityGraphStable, winnerIdForSorting])
  
  // Helper: check if frame is connected to current winner
  const isConnected = (frameIndex: number): boolean => {
    // ✅ FIXED: Winner is always "connected" (to itself)
    if (frameIndex === winnerIdForSorting) {
      return true
    }
    return connectedFrameIndices.has(frameIndex)
  }

  // Sort candidates by relationship metrics
  const getSortMetrics = (candidateFrameIndex: number) => {
    const metrics = {
      distance3d: Infinity,
      indexDifference: Infinity,
    }

    if (winnerIdForSorting === undefined) {
      return metrics
    }

    // Calculate raw 3D Euclidean distance (same as balloon tooltip)
    const fromCam = candidates.find(c => c.frameIndex === winnerIdForSorting)?.camera
    const toCam = candidates.find(c => c.frameIndex === candidateFrameIndex)?.camera
    
    if (fromCam?.position && toCam?.position) {
      const [x1, y1, z1] = fromCam.position
      const [x2, y2, z2] = toCam.position
      metrics.distance3d = Math.sqrt(
        (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
      )
    }

    // Calculate frame index continuity (how close in sequence)
    metrics.indexDifference = Math.abs(candidateFrameIndex - winnerIdForSorting)

    return metrics
  }

  const sortedCandidates = useMemo(() => {
    // ✅ FIXED: ALWAYS recalculate - no frozen cache
    // This ensures we have FRESH connectivity data at all times
    // Panning smoothness is less important than correctness
    
    // Pre-compute metrics for all candidates once
    const metricsMap = new Map<number, { distance3d: number; indexDifference: number; isConnected: boolean }>()
    candidates.forEach(c => {
      const metrics = getSortMetrics(c.frameIndex)
      const connected = isConnected(c.frameIndex)
      metricsMap.set(c.frameIndex, { ...metrics, isConnected: connected })
    })
    
    let sorted = [...candidates]
    
    // Sort based on active sort option
    sorted.sort((a, b) => {
      // Winner always goes first
      if (a.isWinner && !b.isWinner) return -1
      if (!a.isWinner && b.isWinner) return 1
      
      // Apply sort option if selected
      if (activeSortOption === 'connected') {
        // ✅ FIXED: For "connected" sort, prioritize connected frames
        // Then sort connected frames by distance, context frames by distance
        const aConnected = metricsMap.get(a.frameIndex)?.isConnected ? 0 : 1
        const bConnected = metricsMap.get(b.frameIndex)?.isConnected ? 0 : 1
        
        // Primary: All connected frames first, then context frames
        if (aConnected !== bConnected) {
          return aConnected - bConnected
        }
        
        // Secondary: Within each group (connected or context), sort by 3D distance
        const metricsA = metricsMap.get(a.frameIndex)!
        const metricsB = metricsMap.get(b.frameIndex)!
        const result = metricsA.distance3d - metricsB.distance3d
        // console.log(`   sort: Frame ${a.frameIndex} vs ${b.frameIndex}: ${metricsA.distance3d.toFixed(2)} vs ${metricsB.distance3d.toFixed(2)} → ${result > 0 ? 'b first' : 'a first'}`)
        return result
      } else if (activeSortOption === '3d-distance') {
        const metricsA = metricsMap.get(a.frameIndex)!
        const metricsB = metricsMap.get(b.frameIndex)!
        return metricsA.distance3d - metricsB.distance3d
      } else if (activeSortOption === 'delta-angle') {
        // TODO: Add delta angle sorting when we have the data
        // For now, just use index difference as proxy
        const metricsA = metricsMap.get(a.frameIndex)!
        const metricsB = metricsMap.get(b.frameIndex)!
        return metricsA.indexDifference - metricsB.indexDifference
      }
      
      // Default sort (no option selected) - use existing logic
      const metricsA = metricsMap.get(a.frameIndex)!
      const metricsB = metricsMap.get(b.frameIndex)!

      const aConnected = metricsA.isConnected ? 0 : 1
      const bConnected = metricsB.isConnected ? 0 : 1
      
      if (aConnected !== bConnected) {
        return aConnected - bConnected
      }

      // For connected frames: if distance < 1.0, prioritize continuity over 3D distance
      if (aConnected === 0) { // Both are connected
        const aCloseDistance = metricsA.distance3d < 1.0
        const bCloseDistance = metricsB.distance3d < 1.0
        
        // If both have close distance, sort by index proximity (continuity)
        if (aCloseDistance && bCloseDistance) {
          const indexDiff = metricsA.indexDifference - metricsB.indexDifference
          if (indexDiff !== 0) return indexDiff
        }
        
        // If only one has close distance, it goes first
        if (aCloseDistance !== bCloseDistance) {
          return aCloseDistance ? -1 : 1
        }
      }

      // Secondary sort: by 3D distance (ascending - closest first)
      return metricsA.distance3d - metricsB.distance3d
    })
    
    return sorted
  }, [candidates, connectedFrameIndices, offsetMapStable, connectivityGraphStable, activeSortOption, winnerIdForSorting])

  // Auto-scroll to keep winner visible in the center
  useEffect(() => {
    if (!scrollContainerRef.current || currentWinnerId === undefined || selectedBalloonFrame !== null) {
      return
    }

    const winnerIndex = sortedCandidates.findIndex(c => c.frameIndex === currentWinnerId)
    if (winnerIndex === -1) {
      return
    }

    const container = scrollContainerRef.current
    const items = container.querySelectorAll('.candidate-item')
    const winnerItem = items[winnerIndex] as HTMLElement

    if (winnerItem) {
      const containerWidth = container.clientWidth
      const itemWidth = winnerItem.clientWidth
      const itemLeft = winnerItem.offsetLeft

      // Calculate scroll position to center the winner item
      const targetScroll = itemLeft - (containerWidth / 2) + (itemWidth / 2)
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      })
    }
  }, [currentWinnerId, sortedCandidates])

  // Attach non-passive wheel listener
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleWheelNonPassive = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      // Multiply delta by accelerator for faster scrolling
      const scrollAmount = Math.abs(e.deltaY) * 1.5
      container.scrollLeft += e.deltaY > 0 ? scrollAmount : -scrollAmount
    }

    container.addEventListener('wheel', handleWheelNonPassive, { passive: false })
    return () => container.removeEventListener('wheel', handleWheelNonPassive)
  }, [])

  const handleDoubleClick = (frameIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    
    const state = useAppStore.getState()
    
    // Don't re-select the currently selected frame - this prevents unnecessary viewport adjustments
    if (state.manuallySelectedFrameIndex === frameIndex) {
      return
    }
    
    // selectFrameWithoutPan now keeps the frame at same screen position
    // Don't call centerViewportOnFrame - it would override the viewport adjustment
    state.selectFrameWithoutPan(frameIndex)
  }

  const handleTouchEnd = (frameIndex: number, e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const currentTime = Date.now()
    const lastTap = lastTapRef.current
    const timeDiff = currentTime - lastTap.time
    
    // If second tap within 300ms on the same element, treat as double-tap
    if (timeDiff < 300 && lastTap.target === e.currentTarget) {
      e.preventDefault()
      const state = useAppStore.getState()
      // selectFrameWithoutPan now keeps the frame at same screen position
      // Don't call centerViewportOnFrame - it would override the viewport adjustment
      state.selectFrameWithoutPan(frameIndex)
      lastTapRef.current = { time: 0, target: null } // Reset
    } else {
      lastTapRef.current = { time: currentTime, target: e.currentTarget }
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent click from bubbling to parent elements
    e.stopPropagation()
  }

  const handleNumberClick = (frameIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    // Pin the balloon (it stays open until user closes it)
    balloonPinnedRef.current = true
    onBalloonSelect?.(frameIndex)
  }

  const handleNumberTouchEnd = (frameIndex: number, e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation()
    // Pin the balloon (it stays open until user closes it)
    balloonPinnedRef.current = true
    onBalloonSelect?.(frameIndex)
  }

  const handleNumberHover = (frameIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    // Only show balloon on hover if it's not already pinned
    if (!balloonPinnedRef.current) {
      onBalloonSelect?.(frameIndex)
    }
  }

  const handleNumberHoverLeave = () => {
    // Only hide balloon on hover leave if it's not pinned
    if (!balloonPinnedRef.current && selectedBalloonFrame !== null) {
      onBalloonSelect?.(null)
    }
  }

  const handleItemHover = (frameIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    
    // Always bring frame to front in viewer on hover
    onViewportHover?.(frameIndex)
    
    // Also update balloon preview if balloon is active
    if (selectedBalloonFrame !== null) {
      onBalloonHover?.(frameIndex)
    }
  }

  const handleItemHoverLeave = () => {
    // Clear viewport hover when leaving item
    onViewportHover?.(null)
  }

  const handleItemClick = (frameIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    // Bring frame to front when clicked (only if balloon is not active)
    if (selectedBalloonFrame === null) {
      onViewportHover?.(frameIndex)
    }
  }

  // 🆕 Render active filters as badges
  const renderActiveFilters = () => {
    const filters: string[] = []
    
    if (navigationSettings?.useConnectivityFiltering) {
      filters.push('📡 Connectivity')
    }
    
    if (navigationSettings?.use3DDistanceFilter) {
      filters.push(`📏 3D≤${navigationSettings.max3DDistance.toFixed(1)}`)
    }
    
    if (navigationSettings?.useDeltaAngleFilter) {
      filters.push(`🧭 Angle≤${navigationSettings.maxDeltaAngle}°`)
    }
    
    if (navigationSettings?.distanceThresholdPercent !== undefined && navigationSettings.distanceThresholdPercent > 0) {
      filters.push(`⭕ Threshold${navigationSettings.distanceThresholdPercent}%`)
    }
    
    if (filters.length === 0) {
      return <div style={{ color: '#888', fontSize: '12px' }}>No active filters</div>
    }
    
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {filters.map((filter, idx) => (
          <span
            key={idx}
            style={{
              padding: '4px 8px',
              background: '#7ee787',
              color: '#000',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            {filter}
          </span>
        ))}
      </div>
    )
  }

  // 🆕 Check if a frame passes the current NAVIGATION filters (for orange border display)
  // This should use navigationSettings, NOT the pre-filtered connectedFrameIndices
  if (candidates.length === 0) {
    return (
      <div className="candidate-list">
        <div className="candidate-list__empty">
          No candidate frames
        </div>
      </div>
    )
  }

  // 🔍 Debug: Check if frame 0 is in candidates
  // const hasFrame0 = candidates.some(c => c.frameIndex === 0)
  // const frame0Data = candidates.find(c => c.frameIndex === 0)
  // console.log('📋 CandidateFrameList received candidates:', {
  //   count: candidates.length,
  //   hasFrame0,
  //   frame0Data: frame0Data ? { idx: frame0Data.frameIndex, isWinner: frame0Data.isWinner } : 'NOT FOUND',
  //   currentWinnerId,
  //   currentWinnerInCandidates: candidates.some(c => c.frameIndex === currentWinnerId)
  // })

  return (
    <div className="candidate-list">
      {/* CENTER SECTION: Filters and Candidates */}
      <div className="candidate-list__center-section">
        <div className="candidate-list__label">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span>
              Candidates: {candidates.length} | Filtered: <span style={{ color: '#FF9500', fontWeight: 'bold' }}>{candidates.filter(c => c.passedFilters).length}</span> | Connected: {connectedFrameIndices.size} | Context: <span style={{ color: '#6B9AFF', fontWeight: 'bold' }}>{contextFrameCount}</span> | Viewport: {viewportFrameCount}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '4px' }}>
            {/* Settings Button */}
            <button
              onClick={() => onShowNavigationBalloon?.()}
              title="Open Navigation Settings Balloon"
              style={{
                background: '#7ee787',
                border: '1px solid #4CAF50',
                color: '#000',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease-out',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#9ee8a0'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#7ee787'
              }}
            >
              ⚙️ Settings
            </button>

            {/* Active filters display - inline */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '11px' }}>
              {renderActiveFilters()}
            </div>

            {/* Sort options - compact horizontal layout */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto', flexWrap: 'nowrap', flexShrink: 0 }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#888', whiteSpace: 'nowrap' }}>Sort:</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', padding: '2px 6px', background: activeSortOption === 'connected' ? '#444' : 'transparent', borderRadius: '3px', transition: 'background 0.2s ease', flexShrink: 0 }}>
                <input type="checkbox" checked={activeSortOption === 'connected'} onChange={() => setActiveSortOption(activeSortOption === 'connected' ? null : 'connected')} style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
                <span>📡</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', padding: '2px 6px', background: activeSortOption === '3d-distance' ? '#444' : 'transparent', borderRadius: '3px', transition: 'background 0.2s ease', flexShrink: 0 }}>
                <input type="checkbox" checked={activeSortOption === '3d-distance'} onChange={() => setActiveSortOption(activeSortOption === '3d-distance' ? null : '3d-distance')} style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
                <span>📏</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', padding: '2px 6px', background: activeSortOption === 'delta-angle' ? '#444' : 'transparent', borderRadius: '3px', transition: 'background 0.2s ease', flexShrink: 0 }}>
                <input type="checkbox" checked={activeSortOption === 'delta-angle'} onChange={() => setActiveSortOption(activeSortOption === 'delta-angle' ? null : 'delta-angle')} style={{ cursor: 'pointer', width: '14px', height: '14px' }} />
                <span>🧭</span>
              </label>
            </div>
          </div>
        </div>
        
        <div
          className="candidate-list__scroll-container"
          ref={scrollContainerRef}
          onClick={handleClick}
        >
        {(() => {
          // console.log('🎯 CandidateFrameList rendering:', {
          //   totalCandidates: sortedCandidates.length,
          //   currentWinner: currentWinnerId,
          //   sortedFirst5: sortedCandidates.slice(0, 5).map(c => ({ idx: c.frameIndex, isWinner: c.isWinner })),
          // })
          return sortedCandidates.map((candidate) => {
            // All frames in candidates array are ALREADY filtered by NewModeViewer
            // So they all get orange borders (or green if winner)
            return (
            <div
              key={`candidate-${candidate.frameIndex}`}
              className={`candidate-item ${candidate.isWinner ? 'candidate-item--winner' : ''} ${isConnected(candidate.frameIndex) ? 'candidate-item--connected' : ''}`}
              onDoubleClick={(e) => handleDoubleClick(candidate.frameIndex, e)}
              onTouchEnd={(e) => handleTouchEnd(candidate.frameIndex, e)}
              onMouseEnter={(e) => handleItemHover(candidate.frameIndex, e)}
              onMouseLeave={handleItemHoverLeave}
              onClick={(e) => {
                handleClick(e)
                handleItemClick(candidate.frameIndex, e)
              }}
              style={{
                cursor: 'default',
                border: candidate.isWinner 
                  ? '3px solid #00FF00'  // Green for selected frame
                  : candidate.passedFilters
                    ? '3px solid #FF9500'  // Orange for connected frames (passed filters)
                    : candidate.isContextFrame
                      ? '3px solid #0000FF'  // Blue for context frames
                      : '1px solid #ccc',  // Gray for filtered out frames
                transition: 'border 0.2s ease-out',
                userSelect: 'none',
              }}
            >
              {/* Frame thumbnail */}
              <img
                src={candidate.imagePath}
                alt={`Frame ${candidate.frameIndex}`}
                className="candidate-item__image"
                draggable={false}
              />

              {/* Frame number overlay */}
              <div 
                className="candidate-item__number"
                onClick={(e) => handleNumberClick(candidate.frameIndex, e)}
                onTouchEnd={(e) => handleNumberTouchEnd(candidate.frameIndex, e)}
                onDoubleClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => handleNumberHover(candidate.frameIndex, e)}
                onMouseLeave={handleNumberHoverLeave}
                style={{ cursor: 'pointer' }}
                title="Click to see frame relationship, hover to preview"
              >
                {candidate.frameIndex}
              </div>
            </div>
          )
          })
        })()}
        </div>
      </div>

      {/* RIGHT SIDEBAR: Pan Controls */}
      <div className="candidate-list__right-sidebar">
        <CanvasPanControls onPan={handleCanvasPan} onZoom={handleCanvasZoom} />
      </div>
    </div>
  )
}

